'use client';

/**
 * Paywall Component
 * Displayed when user tries to access Pro features without subscription
 */

import { useRouter } from 'next/navigation';
import { useAuth } from '@/lib/auth-provider';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Crown, Check, Sparkles, Zap, Shield, ArrowLeft } from 'lucide-react';
import { useLanguage } from '@/lib/i18n/language-provider';
import { ScrollReveal } from '@/components/scroll-reveal';
import { navigateOrOpenBrowser } from '@/lib/tauri-navigation';

interface PaywallProps {
  message?: string;
}

export function Paywall({ message }: PaywallProps) {
  const { user } = useAuth();
  const router = useRouter();
  const { t } = useLanguage();

  const handleUpgrade = async () => {
    const path = !user ? '/login' : '/pro';
    await navigateOrOpenBrowser(path, router);
  };

  const features = [
    {
      icon: <Sparkles className="h-5 w-5" />,
      text: t('paywall.fullSynoptic'),
    },
    {
      icon: <Zap className="h-5 w-5" />,
      text: t('paywall.multiGridExternal'),
    },
    {
      icon: <Shield className="h-5 w-5" />,
      text: t('paywall.unlimitedSharedSessions'),
    },
    {
      icon: <Check className="h-5 w-5" />,
      text: t('paywall.highResExports'),
    },
    {
      icon: <Check className="h-5 w-5" />,
      text: t('paywall.prioritySupport'),
    },
    {
      icon: <Check className="h-5 w-5" />,
      text: t('paywall.earlyAccess'),
    },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden bg-black">
      {/* Animations de couleurs en arrière-plan - très sombres */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-[10%] w-96 h-96 bg-[#3b82f6] rounded-full blur-[128px] opacity-5 animate-[pulse_8s_ease-in-out_infinite]" />
        <div className="absolute top-[20%] right-[15%] w-80 h-80 bg-[#8b5cf6] rounded-full blur-[128px] opacity-5 animate-[pulse_8s_ease-in-out_infinite_1s]" />
        <div className="absolute bottom-[30%] left-[20%] w-72 h-72 bg-[#ef4444] rounded-full blur-[128px] opacity-5 animate-[pulse_8s_ease-in-out_infinite_2s]" />
        <div className="absolute bottom-[10%] right-[10%] w-80 h-80 bg-[#22c55e] rounded-full blur-[128px] opacity-5 animate-[pulse_8s_ease-in-out_infinite_3s]" />
      </div>

      {/* Grilles en arrière-plan très subtiles */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff04_1px,transparent_1px),linear-gradient(to_bottom,#ffffff04_1px,transparent_1px)] bg-[size:80px_80px]" />

      {/* Back button */}
      <button
        onClick={() => router.push('/')}
        className="absolute top-6 left-6 z-20 flex items-center gap-2 px-4 py-2 rounded-lg bg-background/80 backdrop-blur-sm border border-border/50 hover:bg-background transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        <span className="text-sm font-medium">{t('common.back') || 'Retour'}</span>
      </button>

      {/* Carte centrée */}
      <div className="relative z-10 w-full max-w-4xl">
        <ScrollReveal animation="zoom-blur" duration={1}>
          <div className="relative group">
            {/* Effet de glow */}
            <div className="absolute -inset-2 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 blur-xl opacity-50 group-hover:opacity-75 transition" />
            
            {/* Carte principale */}
            <div className="relative border-4 border-foreground/20 bg-black/90 backdrop-blur-sm p-8 md:p-12">
              <div className="text-center space-y-8">
                {/* Icône */}
                <div className="mx-auto w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
                  <Crown className="h-12 w-12 text-white" />
                </div>

                {/* Titre */}
                <div>
                  <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-3">
                    {t('paywall.upgradeTitle')}
                  </h1>
                  <p className="text-lg text-muted-foreground">
                    {message || t('paywall.unlockFeatures')}
                  </p>
                </div>

                {/* Features Grid */}
                <div className="grid md:grid-cols-2 gap-3 max-w-2xl mx-auto">
                  {features.map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 p-3 border border-foreground/10 hover:border-foreground/30 transition-all text-left"
                    >
                      <div className="text-primary mt-0.5">{feature.icon}</div>
                      <span className="text-sm">{feature.text}</span>
                    </div>
                  ))}
                </div>

                {/* Prix et CTA */}
                <div className="border-t border-foreground/10 pt-8 space-y-6">
                  <div>
                    <div className="text-5xl md:text-6xl font-bold mb-2">9,99€</div>
                    <div className="text-lg text-muted-foreground">{t('paywall.perMonth')}</div>
                  </div>

                  <Button
                    size="lg"
                    onClick={handleUpgrade}
                    className="text-lg px-12 py-6 h-auto bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white shadow-lg w-full max-w-md mx-auto"
                  >
                    <Crown className="mr-3 h-6 w-6" />
                    {t('paywall.upgradeNow')}
                  </Button>

                  <p className="text-sm text-muted-foreground">
                    {t('paywall.securePayment')} • {t('paywall.cancelAnytime')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </div>
  );
}

